<!-- Sits in the page's side panel, which names it and frames it; this is its contents only. -->
<section class="flex flex-col gap-1.5">
  @if (rows().length === 0) {
    <p class="text-sm text-neutral-400">{{ "guaranteeV2.message.noBreaches" | translate }}</p>
  } @else {
    <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
      @if (errorCount() > 0) {
        <guarantees-chip
          tone="error"
          icon="error"
          [tooltip]="'guaranteeV2.message.breachErrorsHint' | translate: { count: errorCount() }"
          >{{ errorCount() }}</guarantees-chip
        >
      }
      @if (warningCount() > 0) {
        <guarantees-chip
          tone="warning"
          icon="warning"
          [tooltip]="'guaranteeV2.message.breachWarningsHint' | translate: { count: warningCount() }"
          >{{ warningCount() }}</guarantees-chip
        >
      }
    </div>

    <ul class="flex flex-col gap-1.5">
      @for (row of rows(); track row.key) {
        <li class="flex flex-col gap-1 rounded-lg border border-neutral-200 bg-neutral-0 px-2 py-1.5">
          <!-- What it is, and how serious. -->
          <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
            <guarantees-chip
              [tone]="tone(row.severity)"
              [icon]="icon(row.severity)"
              [tooltip]="'guaranteeV2.label.severity' + row.severity | translate"
              >{{ "guaranteeV2.label.severity" + row.severity | translate }}</guarantees-chip
            >

            <span class="min-w-0 flex-1 text-sm font-semibold text-neutral-800">{{ "guaranteeV2.message.breachSummary" + row.code | translate }}</span>

            <button
              class="inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-sm font-medium text-primary-600 transition-colors duration-150 hover:bg-primary-50 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none motion-reduce:transition-none"
              type="button"
              [matTooltip]="(row.hasAppointmentDate ? 'guaranteeV2.message.openBreachSourceHint' : 'guaranteeV2.message.noAppointmentDateHint') | translate"
              (click)="openSource(row)"
            >
              <mat-icon class="h-3.5! w-3.5! text-sm! leading-3.5!">open_in_full</mat-icon>
              <span
                class="tabular-nums"
                [class.italic]="!row.hasAppointmentDate"
                [class.text-neutral-500]="!row.hasAppointmentDate"
                >{{ row.visitDate | date: "d MMM yyyy" }}</span
              >
            </button>
          </div>

          <!-- What to do about it. -->
          <p class="text-sm text-neutral-500">{{ "guaranteeV2.message.breachHint" + row.code | translate }}</p>

          <!-- Where it sits, and which objects it points at. -->
          <div class="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-neutral-400">
            @if (row.sourceKind === refKinds.Prescription) {
              <span
                class="max-w-56 truncate text-neutral-600"
                [matTooltip]="'guaranteeV2.label.prescription' | translate"
                >{{ row.prescriptionName || ("guaranteeV2.label.prescription" | translate) }}</span
              >
            } @else {
              <span [matTooltip]="'guaranteeV2.message.breachOnGuaranteeHint' | translate">{{ "guaranteeV2.label.guarantee" | translate }}</span>
            }

            @if (row.diagnosis?.code) {
              <span class="rounded bg-neutral-100 px-1 font-mono font-semibold text-neutral-600">{{ row.diagnosis.code }}</span>
            }
            @if (row.partnerName) {
              <span class="max-w-40 truncate">{{ row.partnerName }}</span>
            }

            @for (reference of row.references; track $index) {
              @if (isNavigable(reference)) {
                <button
                  class="inline-flex items-center gap-0.5 rounded-full border border-primary-200 py-0.5 pr-1.5 pl-1 font-mono font-medium text-primary-600 transition-colors duration-150 hover:bg-primary-50 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none motion-reduce:transition-none"
                  type="button"
                  [matTooltip]="'guaranteeV2.message.openReferenceHint' | translate: { kind: 'guaranteeV2.label.ref' + reference.refKind | translate, id: reference.id }"
                  (click)="openReference(reference)"
                >
                  <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5!">north_east</mat-icon>
                  {{ "guaranteeV2.label.ref" + reference.refKind | translate }}&nbsp;{{ reference.id.slice(0, 8) }}
                </button>
              } @else {
                <span
                  class="inline-flex items-center gap-0.5 rounded-full border border-dashed border-neutral-200 py-0.5 pr-1.5 pl-1 font-mono text-neutral-400"
                  [matTooltip]="'guaranteeV2.message.referenceNotNavigableHint' | translate: { kind: 'guaranteeV2.label.ref' + reference.refKind | translate, id: reference.id }"
                >
                  {{ "guaranteeV2.label.ref" + reference.refKind | translate }}&nbsp;{{ reference.id.slice(0, 8) }}
                </span>
              }
            }

            <span
              class="ml-auto shrink-0 tabular-nums"
              [matTooltip]="
                row.detectedByRun ? ('guaranteeV2.message.detectedByRunHint' | translate: { run: row.detectedByRun }) : ('guaranteeV2.message.detectedAtHint' | translate)
              "
              >{{ row.detectedAt | date: "d MMM HH:mm" }}</span
            >
          </div>
        </li>
      }
    </ul>
  }
</section>
